import { StackScreenProps } from "@react-navigation/stack";
import {
  MUTATION_CONVERT_PROFORMA_TO_SALE,
  QUERY_PROFORMA_BY_SALE_ID,
  QUERY_SALE_PROFORMA,
} from "../../../../utilities/proformas";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../ResourcPageSkeleton";
import { useFocusEffect } from "@react-navigation/native";
import React from "react";
import {
  Sale,
  SaleProforma,
} from "@towersystems/roam-common/lib/generated-types";
import {
  Card,
  Divider,
  Text,
  Page,
  Stack,
  FlatActionList,
  TileButtonActionList,
  useWithAppState,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Spacings, View } from "react-native-ui-lib";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
  moneyToFormattedString,
  toTitleCase,
} from "@towersystems/roam-common/lib/shared-utils";
import { AntDesign, EvilIcons } from "@expo/vector-icons";
import { useSwitchSale } from "../../utils/sale-controller";
import { FlatList } from "react-native";

// view a proforma by id or the associated sale id

export interface ViewProformaScreenProps
  extends StackScreenProps<
    {
      ViewProformaScreen: {
        proformaId?: string;
        saleId?: string;
      };
    },
    "ViewProformaScreen"
  > {}

export const ViewProformaScreen = ({
  route,
  navigation,
}: ViewProformaScreenProps) => {
  const [proforma, setProforma] = React.useState<SaleProforma | undefined>(
    undefined
  );
  const id = route.params?.proformaId;
  const saleId = route.params?.saleId;

  const [q] = useLazyQuery<{
    saleProforma: SaleProforma;
  }>(QUERY_SALE_PROFORMA);
  const [bySaleQ] = useLazyQuery<{
    proformaBySaleId: SaleProforma | undefined;
  }>(QUERY_PROFORMA_BY_SALE_ID);

  const transitionEnded = useHasTransitionEnded(navigation);

  const [mutationConvertProforma] = useMutation<{
    convertProformaToSale: Sale;
  }>(MUTATION_CONVERT_PROFORMA_TO_SALE);

  const switchSale = useSwitchSale();

  const {
    appState: { terminalId, locationId },
  } = useWithAppState();

  const { showMessage } = useFlashMessages();

  useFocusEffect(
    React.useCallback(() => {
      if (saleId) {
        bySaleQ({
          variables: {
            saleId,
          },
        })
          .then((res) => {
            setProforma(res.data?.proformaBySaleId);
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        q({
          variables: {
            id: id ?? "",
          },
        })
          .then((res) => {
            setProforma(res.data?.saleProforma);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }, [id, saleId])
  );

  const handleOnConvertSale = (proforma: SaleProforma) => {
    mutationConvertProforma({
      variables: {
        id: proforma.id,
        options: {
          terminalId,
          locationId,
        },
      },
    })
      .then((res) => {
        if (res.data?.convertProformaToSale) {
          switchSale(res.data.convertProformaToSale);
          navigation.getParent()?.getParent()?.goBack();
        } else {
          showMessage({
            message: "Failed to convert proforma to sale",
            severity: "error",
          });
        }
      })
      .catch((err) => {
        showMessage({ message: err?.toString(), severity: "error" });
      });
  };

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if (!proforma || false == transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={navigation}
        content={ResourcePageSkeleton}
        headerShown={false}
      />
    );
  }

  const customerMarkup = proforma.data?.customerDetails ? (
    <Card title="Customer">
      <View>
        <Text variant="heading1">
          {toTitleCase(proforma.data?.customerDetails?.customerName ?? "")}
        </Text>
      </View>
    </Card>
  ) : null;

  const itemMarkup = (
    <Card title="Items">
      <FlatList
        ItemSeparatorComponent={Divider}
        scrollEnabled={true}
        data={proforma?.data?.items ?? []}
        renderItem={(item) => {
          return (
            <View>
              <View flex row>
                <View
                  style={{
                    alignItems: "center",
                    flexBasis: "100%",
                    flexShrink: 1,
                  }}
                  row
                >
                  <Text
                    variant="heading1"
                    numberOfLines={1}
                    ellipsizeMode="head"
                  >
                    {`${item.item.quantity}x  `}{" "}
                    {toTitleCase(item.item.description ?? "")}
                  </Text>
                </View>
              </View>
            </View>
          );
        }}
      />
    </Card>
  );

  const secondaryTitleMarkup = (
    <View>
      {proforma.location?.name ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: Spacings.s1,
          }}
        >
          <EvilIcons
            name="location"
            size={18}
            color="black"
            style={{
              marginRight: Spacings.s1,
            }}
          />
          <Text variant="heading5" fontWeight="bold">
            {proforma.location?.name}
          </Text>
        </View>
      ) : null}
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <AntDesign
          name="lock"
          size={15}
          color="black"
          style={{
            marginRight: Spacings.s1,
          }}
        />
        <Text color="subdued">
          {formatDateToCalendar(proforma.data?.lockedDate, fullDateTimeFormat)}
        </Text>
      </View>
    </View>
  );

  const totalsMarkup = (
    <Card title="Totals">
      <FlatActionList
        items={[
          {
            key: "total",
            title: "Quote Total",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(proforma.data?.quoteTotal)}
              </Text>
            ),
          },
          {
            key: "balance",
            title: "Balance Due",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(proforma.data?.balance)}
              </Text>
            ),
          },
        ]}
      />
    </Card>
  );

  const actionsMarkup = !proforma.saleId && (
    <TileButtonActionList
      actions={[
        {
          isAction: true,
          id: "create_sale",
          label: "Pay",
          onPress: () => handleOnConvertSale(proforma),
        },
      ]}
    />
  );

  return (
    <Page
      title={`Proforma - #${proforma?.identifier}`}
      secondaryTitle={secondaryTitleMarkup}
    >
      <Stack vertical>
        {actionsMarkup}
        {customerMarkup}
        {itemMarkup}
        {totalsMarkup}
      </Stack>
    </Page>
  );
};
